import { Loader2 } from "lucide-react";
import { lazy, Suspense, useEffect } from "react";
import { useSiteQuery, type SiteInfo } from "@/hooks/use-site";
import { applySiteTheme } from "@/lib/site-theme";
import { SiteConnectionError, SiteUnavailable } from "./site-unavailable";

const TenantApp = lazy(async () => ({ default: (await import("./tenant-app")).TenantApp }));
const AdminApp = lazy(async () => ({ default: (await import("@/features/platform/admin-app")).AdminApp }));
const LandingApp = lazy(async () => ({ default: (await import("@/features/landing/landing-app")).LandingApp }));

function titleFor(site: SiteInfo) {
  if (site.mode === "single") return null; // keep index.html's title (production)
  switch (site.kind) {
    case "landing":
      return `${site.platform.name} — Order management for growing businesses`;
    case "admin":
      return `${site.platform.name} Admin`;
    case "tenant":
      return `${site.tenant?.name ?? site.platform.name} — Orders`;
    default:
      return "Site unavailable";
  }
}

function Loading() {
  return (
    <div className="flex min-h-svh items-center justify-center" role="status" aria-label="Loading">
      <Loader2 className="text-muted-foreground size-5 animate-spin" aria-hidden />
    </div>
  );
}

/**
 * Decides what this address shows before anything else renders:
 * the Invora landing page, the admin dashboard, a business's app, or
 * "This site is unavailable" (never a sign-in form for an unknown address).
 */
export function SiteGate() {
  const site = useSiteQuery();
  const data = site.data;

  useEffect(() => {
    if (!data) return;
    applySiteTheme({ invora: data.mode === "multi", brandColor: data.kind === "tenant" ? data.tenant?.brandColor : null });
    const title = titleFor(data);
    if (title) document.title = title;
  }, [data]);

  if (!data) return site.isError ? <SiteConnectionError onRetry={() => void site.refetch()} /> : <Loading />;

  return (
    <Suspense fallback={<Loading />}>
      {data.kind === "tenant" ? <TenantApp /> : data.kind === "admin" ? <AdminApp /> : data.kind === "landing" ? <LandingApp /> : <SiteUnavailable />}
    </Suspense>
  );
}
